import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  boardFormDataToBoardDetailPreview,
  boardFormDataToPayload,
} from '@/utils/board';
import { BoardFormData, BooleanOption } from '@/hooks/board/useBoardForm';
import { useDialog } from '@/hooks/global-tools';
import { useAtom, useAtomValue } from 'jotai';
import { MyInfoState, socialState } from '@/recoil/atom';
import { CategoryType } from '@/types/api';
import {
  useCreateBoardPost,
  useDeleteTempBoard,
  useGetEditDetailBoard,
  useUpdateBoardPost,
} from '@/hooks/apiHooks';
import { BoardDetailData } from '@/types/board';
import { BoardFormProps } from '@/components/Board/CreateBoardModal/BoardForm';
import { formatAs, getDateFormat, getFormattedNow } from '@/utils/format';
import { isDevApi } from '@/constant';
import { boardWebPushCall } from '@/hooks/useNotificationTopics';
import useGlobalMutation from '@/hooks/useGlobalMutation';
import { AxiosError } from 'axios';

export type FormAction = {
  documentId?: string;
  setDocumentId: Dispatch<SetStateAction<string>>;
  isGetEditDetailBoardLoading: boolean;
  saveTime: string | undefined;
  validateStartDate: () => boolean;
  getPreviewData: (categoryOptions: CategoryType[]) => BoardDetailData;
  deleteTempData: (ids: string[]) => Promise<void>;
  save: (
    showErrorMessage: boolean,
    isTemporary?: boolean,
  ) => Promise<string | undefined>;
  submit: () => Promise<void>;
};

export type useBoardFormActionProps = {
  documentId?: string;
  onSubmit: (documentId: string) => void;
} & Pick<BoardFormProps, 'methods'>;

const useBoardFormAction = (props: useBoardFormActionProps): FormAction => {
  const { documentId: _documentId, onSubmit, methods } = props;
  const { reset, getValues, handleSubmit } = methods;
  const isNew = !_documentId;

  const globalMutate = useGlobalMutation();
  const userInfo = useAtomValue(MyInfoState);
  const dialog = useDialog();
  const [state, setState] = useAtom(socialState);
  const isInit = useRef(true);

  const [documentId, setDocumentId] = useState<string>(_documentId || '');
  const {
    isLoading: isGetEditDetailBoardLoading,
    data: editBoardData,
    mutate: mutateEditBoardData,
  } = useGetEditDetailBoard(isNew, documentId);
  const [saveTime, setSaveTime] = useState<string>();

  useEffect(() => {
    if (!isInit.current || isGetEditDetailBoardLoading || !editBoardData) {
      return;
    }
    isInit.current = false;

    reset(editBoardData);
    if (isNew) setSaveTime(getDateFormat(editBoardData.updatedTime));
  }, [isGetEditDetailBoardLoading, editBoardData]);

  const getDefaultDisplayStartedAt = useCallback(
    (displayStartedAt: Date | null) => {
      const today = new Date();
      if (isNew || !displayStartedAt) return today;

      return displayStartedAt > today ? today : displayStartedAt;
    },
    [isNew],
  );

  // 임시 저장 데이터 삭제
  const deleteTempData = async (ids: string[]) => {
    try {
      await useDeleteTempBoard(ids);
    } catch (e) {
      dialog.show({ message: '에러가 발생했어요. 잠시후 다시 시도해주세요' });
    }
  };

  // 게시글 등록일자 validation
  const validateStartDate = (): boolean => {
    const useThumbnailDisplayTime = getValues('useThumbnailDisplayTime');
    const thumbnailDisplayStartedAt = getValues('thumbnailDisplayStartedAt');
    const displayTime = getValues('displayTime');
    const useUploadTime = getValues('useUploadTime');

    if (
      useThumbnailDisplayTime === BooleanOption.FALSE ||
      useUploadTime === BooleanOption.FALSE
    ) {
      return true;
    }

    const isValid =
      !displayTime ||
      !thumbnailDisplayStartedAt ||
      Boolean(thumbnailDisplayStartedAt >= displayTime);

    if (!isValid) {
      dialog.show({
        message:
          '홈화면 노출 날짜가 게시글 등록일보다\n빠르게 설정되어 있어요. 날짜를 조정해 주세요.',
      });
    }

    return isValid;
  };

  const getPreviewData = (categoryOptions: CategoryType[]) => {
    const data = getValues();
    return boardFormDataToBoardDetailPreview(
      data,
      categoryOptions,
      userInfo,
      getDefaultDisplayStartedAt(data.displayStartedAt),
    );
  };

  const save = async (
    showErrorMessage: boolean,
    isTemporary?: boolean,
  ): Promise<string | undefined> => {
    dialog.hideAll();

    const data = getValues();

    // isTemporary가 명시되지 않으면:
    // - 새 글 작성 플로우: true (임시저장 상태 유지)
    // - 기존 글 수정 플로우: false (기존 공개 상태 유지)
    const useTemporary = isTemporary === undefined ? isNew : isTemporary;

    try {
      const payload = boardFormDataToPayload(
        data,
        useTemporary,
        getDefaultDisplayStartedAt(data.displayStartedAt),
      );

      if (documentId) {
        // 이미 저장된 내용이 있을 때 (수정)
        await useUpdateBoardPost({ ...payload, documentId });
        setSaveTime(getDateFormat(new Date()));
        await mutateEditBoardData();

        // 공개 게시글로 저장하는 경우 캐시 갱신
        if (!useTemporary) {
          globalMutate('api/v1/board', 'createdBy');
          globalMutate('api/v1/board/filter/count', 'createdBy');
          globalMutate('api/v1/board', 'isBookmark');
          globalMutate('api/v1/board/filter/count', 'isBookmark');
        }

        return documentId;
      } else {
        // 처음 저장
        const { id, createdAt, updatedAt } = await useCreateBoardPost(payload);
        const updatedDate = new Date(updatedAt);

        reset((prev) => ({
          ...prev,
          id,
          createdTime: new Date(createdAt),
          updatedTime: updatedDate,
        }));

        setSaveTime(getDateFormat(updatedDate));
        setDocumentId(id);

        // 공개 게시글로 저장하는 경우 캐시 갱신
        if (!useTemporary) {
          globalMutate('api/v1/board', 'createdBy');
          globalMutate('api/v1/board/filter/count', 'createdBy');
          globalMutate('api/v1/board', 'isBookmark');
          globalMutate('api/v1/board/filter/count', 'isBookmark');
        }

        return id;
      }
    } catch (error) {
      if (showErrorMessage) {
        const e = error as AxiosError;
        const data = e.response?.data as any;
        dialog.show({
          title: `${useTemporary ? '임시저장' : '저장'}에 실패했습니다.`,
          message: data.message,
        });
      }
      return undefined;
    }
  };

  const submit = handleSubmit(async (data: BoardFormData) => {
    dialog.hideAll();

    try {
      const payload = boardFormDataToPayload(
        data,
        false,
        getDefaultDisplayStartedAt(data.displayStartedAt),
      );

      if (documentId) {
        await useUpdateBoardPost({ documentId, ...payload });
        await mutateEditBoardData();
        onSubmit(documentId);
      } else {
        const { id } = await useCreateBoardPost(payload);
        onSubmit(id);
      }

      // 리스트 업데이트를 위한 갱신
      setState({
        ...state,
        currentDate: getFormattedNow(),
      });

      if (!isDevApi && data.useUploadTime === BooleanOption.FALSE) {
        // 리프레쉬 푸쉬 즉시 발송은 예약 게시글이 아니어야 한다.
        boardWebPushCall();
      } else {
        // 예약 게시글 목록과 카운트 갱신
        globalMutate('api/v1/board', 'createdBy');
        globalMutate('api/v1/board/filter/count', 'createdBy');
      }

      // 예약 게시글은 공개 시각에 리프레쉬 푸쉬가 발송되도록 예약한다.
      if (
        !isDevApi &&
        data.useUploadTime === BooleanOption.TRUE &&
        data.displayTime &&
        data.displayTime > new Date()
      ) {
        boardWebPushCall(formatAs(data.displayTime, 'dateTimeMinute'));
      }

      // 즐겨찾기 목록과 카운트 갱신 (게시글이 즐겨찾기에 포함될 수 있으므로 항상 갱신)
      globalMutate('api/v1/board', 'isBookmark');
      globalMutate('api/v1/board/filter/count', 'isBookmark');
    } catch (error) {
      const title = `게시글 ${isNew ? '작성' : '수정'}에 실패했습니다.`;
      const e = error as AxiosError;
      const data = e.response?.data as any;
      if (data && data.code === '0400') {
        dialog.show({
          title,
          message: `해당 게시글은 이미 등록되어 있어 임시저장이 불가능합니다.`,
        });
      } else {
        dialog.show({
          title,
          message: data.message,
        });
      }
    }
  });

  return {
    documentId,
    setDocumentId,
    isGetEditDetailBoardLoading: !editBoardData && isGetEditDetailBoardLoading,
    saveTime,
    deleteTempData,
    validateStartDate,
    getPreviewData,
    save,
    submit,
  };
};

export default useBoardFormAction;
